import {
  SecurityEventDetail,
  SecurityEventSummary,
  type Severity,
  type UpdateEventRequest,
} from '@bartizo/shared';
import { apiPageRequest, apiRequest, type Page } from './client.js';

/**
 * Events (knowledge doc section 15).
 *
 * The query is built here rather than by each caller, for the reason `servers.ts`
 * gives: a filter that does not exist should be a type error, not a parameter the
 * API quietly ignores.
 */

export interface EventFilters {
  server_id?: string;
  group_id?: string;
  category?: string;
  /** A MITRE ATT&CK tactic or technique id (P11.8.6). */
  tactic?: string;
  technique?: string;
  min_severity?: Severity;
  /** Unset shows both; `false` is the triage queue. */
  acknowledged?: boolean;
  /** `me` and `none` are keywords the API resolves; anything else is a user id. */
  assigned_to?: string;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
}

const toQueryString = (filters: EventFilters): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === '') continue;
    params.set(key, typeof value === 'boolean' ? String(value) : String(value));
  }
  const query = params.toString();
  return query.length > 0 ? `?${query}` : '';
};

export const listEvents = (filters: EventFilters = {}): Promise<Page<SecurityEventSummary>> =>
  apiPageRequest(`/events${toQueryString(filters)}`, { schema: SecurityEventSummary });

export const getEvent = (id: string): Promise<SecurityEventDetail> =>
  apiRequest(`/events/${encodeURIComponent(id)}`, { schema: SecurityEventDetail });

/** Section 17: acknowledging, noting and assigning are an operator's, not a viewer's. */
export const updateEvent = (id: string, patch: UpdateEventRequest): Promise<SecurityEventDetail> =>
  apiRequest(`/events/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: patch,
    schema: SecurityEventDetail,
  });
